import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import { getStoredSession } from '@accenturecodefoundry/mfe-pot-shared-auth/core';
import type { JobListing } from 'job-bank-data-access';
import { HttpJobBankApiClient } from 'job-bank-data-access';
import { assetBaseUrl } from '../asset-base-url';
import { loadRuntimeConfig } from '../../runtime-config';
import { uploadDocument } from '../data/documents-api';
import './ResumeUploadPage.css';

export interface ResumeUploadPageProps {
  listing: JobListing;
  onBack: () => void;
  onBackToMatches: () => void;
}

const TOTAL_STEPS = 3;
const CURRENT_STEP = 2;

// Matches job-bank-bff's own MAX_DOCUMENT_SIZE_BYTES -- kept in sync by
// comment, not by a shared constant (frontend and BFF don't share a build).
const MAX_FILE_SIZE_BYTES = 5_242_880;
const ACCEPTED_CONTENT_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/rtf',
  'text/rtf',
  'text/plain', // .txt
];

interface SelectedFile {
  filename: string;
  size: number;
  contentType: string;
  contentBase64: string;
}

// Geometry only, same convention as JobDetailsPage's SkillMatchedIcon --
// SCDS's icon set (scds-icon-paths.ts) has no upload/trash glyph.
function UploadCloudIcon() {
  return (
    <svg viewBox="0 0 24 24" className="resume-upload__dropzone-glyph" aria-hidden="true">
      <path d="M7 18a4.5 4.5 0 0 1-1-8.9 5.5 5.5 0 0 1 10.8-1.7A4 4 0 0 1 17 15.5" />
      <path d="M12 11v8M9 14l3-3 3 3" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" className="resume-upload__trash-glyph" aria-hidden="true">
      <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

// "Use Resume Builder" tab glyph -- a plain sparkle, not an SCDS icon (see
// UploadCloudIcon's own comment).
function SparkleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="resume-upload__tab-glyph" aria-hidden="true">
      <path d="M11 3.5 12.4 8l4.6 1.4-4.6 1.4L11 15l-1.4-4.2L5 9.4l4.6-1.4Z" />
      <path d="M17.5 14.5 18.3 17l2.5.8-2.5.8-.8 2.4-.8-2.4-2.5-.8 2.5-.8Z" />
    </svg>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // "data:<mime>;base64,<data>" -- strip everything before the comma.
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function ResumeUploadPage({ listing, onBack, onBackToMatches }: ResumeUploadPageProps) {
  const locale = useLocale();
  const { t } = useTranslations(assetBaseUrl, locale);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const confirmationHeadingRef = useRef<HTMLHeadingElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<SelectedFile | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (submitted) {
      confirmationHeadingRef.current?.focus();
    } else {
      headingRef.current?.focus();
    }
  }, [submitted]);

  const title = locale === 'fr' ? listing.title.fr : listing.title.en;

  async function handleFiles(files: FileList | null | undefined) {
    const selected = files?.[0];
    if (!selected) return;
    if (selected.size > MAX_FILE_SIZE_BYTES || !ACCEPTED_CONTENT_TYPES.includes(selected.type)) {
      setFileError(t('directApplyResume.dropzone.error'));
      return;
    }
    setFileError(null);
    const contentBase64 = await readFileAsBase64(selected);
    setFile({ filename: selected.name, size: selected.size, contentType: selected.type, contentBase64 });
  }

  async function handleSubmit() {
    if (!file || !consentChecked || submitting) return;
    const session = getStoredSession();
    if (!session) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await uploadDocument('resume', file);
      const runtimeConfig = await loadRuntimeConfig(assetBaseUrl);
      await new HttpJobBankApiClient(runtimeConfig.jobBankBffBaseUrl).apply(listing.id, session.sub);
      setSubmitted(true);
    } catch {
      setSubmitError(t('directApplyResume.submitError'));
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="resume-upload resume-upload--confirmation">
        <div className="resume-upload__confirmation-icon" aria-hidden="true">
          <scds-icon name="check-circle" size="lg" />
        </div>
        <h1 ref={confirmationHeadingRef} tabIndex={-1} className="resume-upload__h1">
          {t('directApplyResume.confirmation.heading')}
        </h1>
        <p className="resume-upload__confirmation-summary">
          {t('directApplyResume.confirmation.summary.beforeTitle')}
          <strong>{title}</strong>
          {t('directApplyResume.confirmation.summary.betweenTitleAndEmployer')}
          {listing.employer}
          {t('directApplyResume.confirmation.summary.afterEmployer')}
          <a href="/dashboard" className="resume-upload__confirmation-link">
            {t('directApplyResume.confirmation.summary.trackLinkText')}
          </a>
          {t('directApplyResume.confirmation.summary.afterTrackLink')}
        </p>
        <p className="resume-upload__confirmation-summary">
          {t('directApplyResume.confirmation.eiNotice.before')}
          <a href="/employment-insurance" className="resume-upload__confirmation-link">
            {t('directApplyResume.confirmation.eiNotice.linkText')}
          </a>
          {t('directApplyResume.confirmation.eiNotice.after')}
        </p>
        <button type="button" className="resume-upload__confirmation-back" onClick={onBackToMatches}>
          {t('directApplyResume.confirmation.backToMatches')}
          <scds-icon name="arrow-right" size="sm" aria-hidden="true" />
        </button>
      </div>
    );
  }

  const canSubmit = file !== null && consentChecked && !submitting;

  return (
    <div className="resume-upload">
      <scds-progress-bar
        current={CURRENT_STEP}
        total={TOTAL_STEPS}
        step-label={t('directApplyResume.heading')}
        label={t('directApply.progressLabel')}
      />

      <section className="resume-upload__heading">
        <h1 ref={headingRef} tabIndex={-1} className="resume-upload__h1">
          {t('directApplyResume.heading')}
        </h1>
        <div aria-hidden="true" className="resume-upload__accent-bar" />
      </section>

      <div className="resume-upload__job-card">
        <div className="resume-upload__job-icon" aria-hidden="true">
          <scds-icon name="briefcase" size="md" />
        </div>
        <div className="resume-upload__job-body">
          <p className="resume-upload__job-eyebrow">{t('directApply.applyingFor')}</p>
          <p className="resume-upload__job-title">{title}</p>
          <p className="resume-upload__job-meta">
            {listing.employer} &middot; {listing.city}, {listing.province}
          </p>
        </div>
      </div>

      <p className="resume-upload__instructions">{t('directApplyResume.instructions')}</p>

      {/* "Use Resume Builder" is a visual placeholder only -- there's no
          resume-builder feature behind it yet. "Upload a resume" is the
          only real mode, so it's always shown active. */}
      <div className="resume-upload__mode-tabs">
        <button type="button" className="resume-upload__mode-tab resume-upload__mode-tab--active" aria-pressed="true">
          <UploadCloudIcon />
          {t('directApplyResume.tabs.upload')}
        </button>
        <button type="button" className="resume-upload__mode-tab" disabled>
          <SparkleIcon />
          {t('directApplyResume.tabs.resumeBuilder')}
        </button>
      </div>

      {!file ? (
        <>
          <div
            role="button"
            tabIndex={0}
            className="resume-upload__dropzone"
            aria-label={t('directApplyResume.dropzone.label')}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              void handleFiles(e.dataTransfer.files);
            }}
          >
            <span className="resume-upload__dropzone-icon-badge" aria-hidden="true">
              <UploadCloudIcon />
            </span>
            <p className="resume-upload__dropzone-title">{t('directApplyResume.dropzone.title')}</p>
            <p className="resume-upload__dropzone-subtitle">{t('directApplyResume.dropzone.subtitle')}</p>
          </div>
          {/* A sibling, not a child, of the dropzone div -- axe's
              nested-interactive check (correctly) flags a focusable
              control inside another interactive control even with
              tabIndex={-1} and aria-hidden, since assistive tech can
              still reach it. */}
          <input
            ref={inputRef}
            type="file"
            tabIndex={-1}
            aria-hidden="true"
            className="resume-upload__input"
            accept=".pdf,.docx,.rtf,.txt"
            onChange={(e) => {
              void handleFiles(e.currentTarget.files);
              e.currentTarget.value = '';
            }}
          />
        </>
      ) : (
        <div className="resume-upload__file-row">
          <span className="resume-upload__file-info">
            <span className="resume-upload__file-name">{file.filename}</span>
            <span className="resume-upload__file-size">{formatFileSize(file.size)}</span>
          </span>
          <button
            type="button"
            className="resume-upload__file-remove"
            aria-label={t('directApplyResume.fileRow.remove', { filename: file.filename })}
            onClick={() => setFile(null)}
          >
            <TrashIcon />
          </button>
        </div>
      )}

      {fileError && (
        <p className="resume-upload__error" role="alert">
          {fileError}
        </p>
      )}

      <label className="resume-upload__consent">
        <input
          type="checkbox"
          checked={consentChecked}
          onChange={(e) => setConsentChecked(e.currentTarget.checked)}
        />
        {t('directApplyResume.consent')}
      </label>

      {submitError && (
        <p className="resume-upload__error" role="alert">
          {submitError}
        </p>
      )}

      <div className="resume-upload__footer">
        <button type="button" className="resume-upload__footer-back" onClick={onBack}>
          <span>&#8249;</span> {t('directApplyResume.back')}
        </button>
        <button type="button" className="resume-upload__submit" disabled={!canSubmit} onClick={handleSubmit}>
          {t('directApplyResume.submit')}
          <scds-icon name="arrow-right" size="sm" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
